import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDomainManager } from '../../config';
import { handleImageErrorWithFallback } from '../../utils/imageManager';
import { renderCtaButton } from '../../utils/ctaHelper';
import { usePageTitle } from '../../hooks/usePageTitle';

const LandingVariant = ({ heroContent, brand }) => {
  const mainImageRef = useRef(null);
  const secondaryImageRef = useRef(null);
  const { domainConfig, cta } = useTheme();
  const domainManager = useDomainManager(brand?.id);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('home');

  // Reset image elements when domain changes
  useEffect(() => {
    if (mainImageRef.current) {
      Object.keys(mainImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete mainImageRef.current.dataset[key];
        }
      });
    }
    if (secondaryImageRef.current) {
      Object.keys(secondaryImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete secondaryImageRef.current.dataset[key];
        }
      });
    }
  }, [brand.id]);

  // Get domain-specific features content using domain manager
  const getDomainFeatures = () => {
    const features = domainManager.getFeatures();
    if (features) {
      return {
        title: features.title,
        subtitle: features.subtitle,
        description: features.description,
        features: features.items
      };
    }
    
    // Fallback for domains without features configured
    return {
      title: 'Why Choose Our Solution?',
      subtitle: 'Everything you need to succeed, all in one place',
      description: 'Comprehensive solution designed to meet your specific requirements with professional support and implementation.',
      features: [
        { title: 'Advanced Technology', description: 'Cutting-edge solutions for modern businesses' },
        { title: 'Expert Support', description: 'Professional support and implementation guidance' },
        { title: 'Proven Results', description: 'Trusted by thousands of satisfied customers' }
      ]
    };
  };

  // Get domain-specific testimonials content using domain manager
  const getDomainTestimonials = () => {
    const testimonials = domainManager.getTestimonials();
    if (testimonials && testimonials.length > 0) {
      return testimonials;
    }
    
    // Fallback for domains without testimonials configured
    return [
      {
        content: "This solution has transformed our business operations completely. Highly recommended!",
        author: "Sarah Johnson",
        role: "CEO",
        company: "TechCorp",
        avatar: null
      },
      {
        content: "The best investment we've made for our company. ROI was immediate and significant.",
        author: "Michael Chen",
        role: "CTO",
        company: "InnovateLab",
        avatar: null
      },
      {
        content: "Outstanding support and incredible results. Our team productivity increased by 40%.",
        author: "Emily Rodriguez",
        role: "Operations Director",
        company: "GrowthCo",
        avatar: null
      }
    ];
  };

  const { mainImage, secondaryImage, title, subtitle, features } = heroContent;
  const domainFeatures = getDomainFeatures();
  const domainTestimonials = getDomainTestimonials();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Landing Page Design */}
      <section className="relative pt-20 pb-32 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="text-white">
              <div className="mb-6">
                <span className="inline-block px-4 py-2 text-sm font-semibold bg-white/20 backdrop-blur-sm rounded-full text-white border border-white/30">
                  {brand.name}
                </span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {h1Title || title}
              </h1>
              
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                {subtitle}
              </p>
              
              {/* Benefits List */}
              <div className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-white/90 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                {renderCtaButton(cta?.primary, 'Request Demo', 'bg-white text-blue-600 px-8 py-4 rounded-xl font-bold hover:bg-white/90 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-center', domainConfig?.shopLink)}
                {renderCtaButton(cta?.secondary, 'Buy Now', 'border-2 border-white text-white px-8 py-4 rounded-xl font-bold hover:bg-white hover:text-blue-600 transition-all duration-300 text-center', domainConfig?.shopLink)}
              </div>

              {/* Trust Indicators */}
              <div className="mt-12 pt-8 border-t border-white/20">
                <p className="text-white/70 text-sm mb-4">Trusted by 1000+ businesses worldwide</p>
                <div className="flex items-center space-x-6">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-12 h-8 bg-white/20 rounded flex items-center justify-center">
                      <span className="text-white/60 text-xs font-semibold">★</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Right Column - Images */}
            <div className="relative">
              {/* Main Image */}
              <div className="relative z-10">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    ref={mainImageRef}
                    src={mainImage} 
                    alt={title}
                    className="w-full h-64 md:aspect-[16/10] object-cover"
                    onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-main')}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              </div>
              
              {/* Secondary Image - Floating */}
              <div className="absolute -bottom-8 -left-8 z-20">
                <div className="relative rounded-xl overflow-hidden shadow-xl">
                  <img 
                    ref={secondaryImageRef}
                    src={secondaryImage} 
                    alt={`${title} - Secondary`}
                    className="w-48 aspect-video object-cover"
                    onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-secondary')}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute top-4 right-4 w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Join Thousands of Happy Customers</h2>
            <p className="text-lg text-gray-600">See what our customers are saying about us</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {domainTestimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.role}, {testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{domainFeatures.title}</h2>
            <p className="text-xl text-gray-600">{domainFeatures.subtitle}</p>
            {domainFeatures.description && (
              <p className="text-base text-gray-500 max-w-3xl mx-auto mt-4">{domainFeatures.description}</p>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {domainFeatures.features.map((feature, index) => (
              <div key={index} className="text-center p-8 bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                  <div className="text-2xl text-white">
                    {feature.icon || (index % 4 === 0 ? '⚡' : index % 4 === 1 ? '🛡️' : index % 4 === 2 ? '📈' : '🎯')}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description || 'Comprehensive solution designed to meet your specific requirements with professional support and implementation.'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of businesses that trust our solution to grow their operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {renderCtaButton(cta?.primary, 'Request Demo', 'px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-100 transition-colors duration-200 text-center', domainConfig?.shopLink)}
            {renderCtaButton(cta?.secondary, 'Buy Now', 'px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-blue-600 transition-colors duration-200 text-center', domainConfig?.shopLink)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingVariant;
